import { Box, Button, LinearProgress, Stack, TextField } from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import GiftcardTable from "./giftcardTable";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useEffect } from "react";
import { GetAll } from "../../../util/request/getAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import { authPost } from "../../../util/request/authPost";

dayjs.extend(utc);
dayjs.extend(timezone);

const GiftcardList = (props) => {
  const [datePick, setDatePick] = useState(dayjs());
  const [giftcardNumber, setGiftcardNumber] = useState("");
  const [giftcardData, setGiftcardData] = useState([]);

  const activateGiftcard = async (card, physicalCardNum) => {
    if (!/^[0-9]{6}$/.test(physicalCardNum)) {
      throw new Error("Physical card number must contain exactly 6 digits.");
    }
    const id = card._id || card.id;
    if (!id) throw new Error("Gift card ID is missing. Refresh and try again.");

    const result = await authPost({
      subLink: `giftcard/${encodeURIComponent(id)}`,
      data: { giftCardStatus: "active", physicalCardNum },
    });
    if (result?.status !== "success") {
      throw new Error(
        result?.response?.data?.message ||
          result?.message ||
          "Unable to activate gift card. Please try again.",
      );
    }
    setGiftcardData((cards) =>
      cards.map((item) =>
        (item._id || item.id) === id
          ? { ...item, giftCardStatus: "active", physicalCardNum }
          : item,
      ),
    );
  };

  useEffect(() => {
    async function runFunc() {
      if (!datePick?.isValid()) return;
      const selectedDate = datePick.format("YYYY-MM-DD");
      const nextDate = datePick.add(1, "day").format("YYYY-MM-DD");
      // Convert each New York midnight independently to account for daylight saving.
      const startUtc = dayjs.tz(selectedDate, "America/New_York").toISOString();
      const endUtc = dayjs.tz(nextDate, "America/New_York").toISOString();
      const query = new URLSearchParams({
        "createdAt[gte]": startUtc,
        "createdAt[lt]": endUtc,
      });

      const response = await GetAll({
        subLink: `giftcard?${query.toString()}`,
      });

      if (response.data) {
        const resultData = response.data.data.sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        );
        const filterData =
          resultData.length > 0 && giftcardNumber.trim().length > 0
            ? resultData.filter((item) =>
                String(item.giftCardNum ?? "")
                  .toLowerCase()
                  .includes(giftcardNumber.trim().toLowerCase()),
              )
            : resultData;
        setGiftcardData(filterData);
      }
    }

    const timeoutId = setTimeout(() => {
      runFunc();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [datePick, giftcardNumber]);

  return (
    <Stack>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{ height: "60px" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <DatePicker
              slotProps={{
                actionBar: { actions: [] },
              }}
              closeOnSelect
              label="Order Date"
              defaultValue={dayjs()}
              value={datePick}
              onChange={(newValue) => {
                setDatePick(newValue);
              }}
              sx={{ height: "64px" }}
            />
          </DemoContainer>
        </LocalizationProvider>
        <TextField
          id="standard-basic"
          label="Redemption Code"
          variant="outlined"
          value={giftcardNumber}
          onChange={(event) => {
            setGiftcardNumber(event.target.value);
          }}
        />
      </Stack>

      <Stack>
        <GiftcardTable
          giftcardData={giftcardData}
          onActivate={activateGiftcard}
        />
      </Stack>
    </Stack>
  );
};

export default GiftcardList;
